import React, { useState, useCallback, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase'; // Assurez-vous que ce fichier Firebase est bien configuré
import { doc, updateDoc, increment } from 'firebase/firestore';
import { FaLock, FaCreditCard, FaSpinner, FaCheckCircle, FaShieldAlt } from 'react-icons/fa';
import './CheckoutForm.css'; // Assurez-vous que ce fichier existe pour ajouter du style
import Footer from '../Footer';

const CheckoutForm = ({ total, clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false); // État pour gérer l'indicateur de chargement
  const navigate = useNavigate(); // Hook pour navigation
  const [paymentError, setPaymentError] = useState(null); // Gérer les erreurs de paiement
  const [isCardComplete, setIsCardComplete] = useState(false); // État pour détecter si la carte est complète

  const updateFirebasePayment = async () => {
    try {
      const paiementDocRef = doc(db, "paiement", "paiement");
      await updateDoc(paiementDocRef, {
        mobile: increment(1), // Incrémente la valeur de "mobile" de +1
      });
      console.log("Mise à jour Firebase réussie !");
    } catch (error) {
      console.error("Erreur lors de la mise à jour de Firebase :", error);
    }
  };

  // Fonction pour gérer les changements dans le champ de carte
  const handleCardChange = (event) => {
    setIsCardComplete(event.complete);
    if (event.error) {
      setPaymentError(event.error.message);
    } else {
      setPaymentError(null);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return; // Stripe.js n'est pas encore chargé
    }

    const cardElement = elements.getElement(CardElement);

    setIsProcessing(true);
    setPaymentError(null); // Réinitialisation des erreurs

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (error) {
        console.error('Erreur lors du paiement :', error.message);
        setPaymentError(error.message); // Afficher l'erreur
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log('Paiement réussi !');
      
        // 🔥 Mise à jour Firebase après un paiement validé
        await updateFirebasePayment();
        // Transmettre les articles et le total global à la page de confirmation
        navigate('/confirmation', {
          state: {
            paymentIntent,
            articles: JSON.parse(localStorage.getItem('articles')), // Tous les articles regroupés
            total, // Total global
          },
        });
      } else {
        setPaymentError('Le paiement n\'a pas pu être effectué.'); // Si le statut n'est pas "succeeded"
      }
    } catch (error) {
      console.error('Erreur lors de la confirmation du paiement :', error);
      setPaymentError('Une erreur s\'est produite lors du traitement du paiement.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaymentRequest = useCallback(async () => {
    if (!stripe || !elements) return;
  
    // Initialisation de la requête de paiement
    const paymentRequest = stripe.paymentRequest({
      country: 'FR', // Pays de l'utilisateur
      currency: 'eur', // Devise
      total: {
        label: 'Total à payer',
        amount: total * 100, // Montant total en centimes
      },
      requestPayerName: true, // Demander le nom de l'utilisateur
      requestPayerEmail: true, // Demander l'email
    });
  
    // Vérifie si l'utilisateur peut utiliser Apple Pay ou Google Pay
    const result = await paymentRequest.canMakePayment();
    if (result) {
      // Monte le bouton uniquement si Apple Pay ou Google Pay est disponible
      const paymentRequestButton = elements.create('paymentRequestButton', {
        paymentRequest,
      });
  
      paymentRequestButton.mount('#payment-request-button');
  
      // Gestion des événements lors du paiement avec Payment Request
      paymentRequest.on('paymentmethod', async (event) => {
        try {
          const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
            payment_method: event.paymentMethod.id,
          });
  
          if (error) {
            console.error('Erreur lors du paiement avec Apple Pay/Google Pay :', error.message);
            event.complete('fail');
          } else if (paymentIntent && paymentIntent.status === 'succeeded') {
            console.log('Paiement réussi avec Apple Pay ou Google Pay !');
          
            // 🔥 Mise à jour Firebase après un paiement validé
            await updateFirebasePayment();          
            event.complete('success');
            navigate('/confirmation', { state: { paymentIntent } });
          } else {
            console.error('Le paiement n’a pas pu être finalisé.');
            event.complete('fail');
          }
        } catch (err) {
          console.error('Erreur lors de la confirmation du paiement :', err.message);
          event.complete('fail');
        }
      });
    }
  }, [stripe, elements, total, clientSecret, navigate]);  
  
  useEffect(() => {
    if (stripe) {
      handlePaymentRequest();
    }
  }, [stripe, handlePaymentRequest]);

  return (
    <div className="checkout-container">
      <div className="payment-card">
        <div className="payment-header">
          <FaCreditCard className="payment-icon" />
          <h3>Méthodes de Paiement</h3>
        </div>
        
        {/* Apple Pay / Google Pay */}
        <div className="payment-request-container">
          <div id="payment-request-button"></div>
        </div>
        
        {/* Divider */}
        <div className="payment-divider">
          <span>ou</span>
        </div>
        
        {/* Card Payment Form */}
        <form className="card-payment-form" onSubmit={handleSubmit}>
          <div className="card-input-section">
            <div className="card-input-header">
              <FaLock className="card-input-icon" />
              <span>Informations de carte</span>
            </div>
            <div className="card-element-container">
              <CardElement 
                onChange={handleCardChange}
                options={{ 
                  hidePostalCode: true,
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                  },
                }} 
              />
            </div>
          </div>

          {/* Submit Button */}
          <button 
            type="submit" 
            disabled={!stripe || isProcessing || !isCardComplete} 
            className={`payment-submit-btn ${isCardComplete ? 'payment-submit-btn--active' : ''}`}
          >
            {isProcessing ? (
              <>
                <FaSpinner className="btn-spinner spinning" />
                Paiement en cours...
              </>
            ) : (
              <>
                <FaCheckCircle className="btn-icon" />
                Payer {total.toFixed(2)} €
              </>
            )}
          </button>

          {/* Error Message */}
          {paymentError && (
            <div className="error-message">
              <p>{paymentError}</p>
            </div>
          )}
        </form>

        {/* Security Message */}
        <div className="security-message">
          <FaShieldAlt className="security-icon" />
          <span>Paiement 100% sécurisé par Stripe</span>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CheckoutForm;
